import React, { useEffect, useState } from 'react';
import { Copy, Download, ExternalLink, QrCode } from 'lucide-react';
import QRCode from 'qrcode';

interface EventShareCardProps {
  eventName: string;
  publicUrl: string;
}

export default function EventShareCard({ eventName, publicUrl }: EventShareCardProps) {
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState('');
  const [isGenerating, setIsGenerating] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let isCancelled = false;

    const generateQrCode = async () => {
      setIsGenerating(true);
      setError('');

      try {
        const dataUrl = await QRCode.toDataURL(publicUrl, {
          width: 280,
          margin: 1,
          color: {
            dark: '#173f3b',
            light: '#ffffff',
          },
        });

        if (!isCancelled) {
          setQrCodeDataUrl(dataUrl);
        }
      } catch (generationError) {
        if (!isCancelled) {
          setError('Failed to generate QR code.');
          setQrCodeDataUrl('');
        }
      } finally {
        if (!isCancelled) {
          setIsGenerating(false);
        }
      }
    };

    generateQrCode();

    return () => {
      isCancelled = true;
    };
  }, [publicUrl]);

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      alert('Public event URL copied to clipboard.');
    } catch (copyError) {
      alert(`Failed to copy URL. Please copy it manually: ${publicUrl}`);
    }
  };

  const handleDownloadQr = () => {
    if (!qrCodeDataUrl) {
      return;
    }

    const link = document.createElement('a');
    link.href = qrCodeDataUrl;
    link.download = `${eventName.toLowerCase().replace(/[^a-z0-9]+/g, '-') || 'event'}-qr.png`;
    link.click();
  };

  return (
    <section className="card share-card">
      <div className="section-heading">
        <h2>
          <QrCode size={20} />
          Share by QR Code
        </h2>
        <p>Create a scannable QR code for the public event page so guests can open it instantly on their phone.</p>
      </div>

      <div className="share-card__layout">
        <div className="share-card__qr">
          {isGenerating ? (
            <div className="share-card__placeholder">
              <div className="spinner spinner--small" />
              <span>Generating QR code...</span>
            </div>
          ) : error ? (
            <div className="share-card__placeholder">
              <span>{error}</span>
            </div>
          ) : (
            <img src={qrCodeDataUrl} alt={`QR code for ${eventName}`} className="share-card__image" />
          )}
        </div>

        <div className="share-card__meta">
          <div className="share-card__url-block">
            <span className="eyebrow">Public URL</span>
            <div className="share-card__url">{publicUrl}</div>
          </div>

          <div className="share-card__actions">
            <button type="button" className="btn" onClick={handleCopyUrl}>
              <Copy size={16} />
              Copy Public URL
            </button>
            <button type="button" className="btn btn-secondary" onClick={handleDownloadQr} disabled={!qrCodeDataUrl}>
              <Download size={16} />
              Download QR PNG
            </button>
            <a className="btn btn-secondary" href={publicUrl} target="_blank" rel="noreferrer">
              <ExternalLink size={16} />
              Open Public Page
            </a>
          </div>

          <div className="share-card__note">
            Place this QR code on signage, welcome boards, or printed programs so guests can scan and find their table quickly.
          </div>
        </div>
      </div>
    </section>
  );
}
